import { useState } from "react";
import { useParams } from "react-router-dom";
import { PatientDocumentDto, PatientDocumentCreateFormData } from "../types/patientDocument";
import { patientDocumentService } from "../services/patientDocumentService";
import { patientDocumentCreateService } from "../services/patientDocumentCreateService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const usePatientDocuments = (overridePatientId?: string) => {
  const { patientId: urlPatientId } = useParams<{ patientId: string }>();
  const patientId = overridePatientId ?? urlPatientId;

  // ΕΝΑ ΚΑΙ ΜΟΝΑΔΙΚΟ request: Επιλέγει αυτόματα endpoint ανάλογα με το αν υπάρχει patientId
  const {
    data: documents = [],
    loading: isFetching,
    error: getError,
    refetch,
    invalidateCache,
  } = useApi<PatientDocumentDto[]>(
    patientId ? `patient-documents-${patientId}` : "patient-documents-all",
    async (signal) => {
      if (patientId) {
        return patientDocumentService.getByPatientId(patientId, signal);
      }
      return patientDocumentService.getAll({}, signal);
    },
    [patientId]
  );

  // Mutation για Create (προσθέτει αυτόματα το patientId από το URL στο payload)
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newDocument: PatientDocumentCreateFormData, signal?: AbortSignal) => {
    const payload = patientId 
      ? { ...newDocument, patientId: Number(patientId) } 
      : newDocument;
      
    return patientDocumentCreateService.create(payload as any, signal);
  });

  // Mutation για Update — multipart/form-data (ίδιο shape με create)
  const {
    execute: updateDocumentExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(async ({ id, data }: { id: number; data: PatientDocumentCreateFormData }, signal?: AbortSignal) => {
    const payload = patientId
      ? { ...data, patientId: Number(patientId) }
      : data;

    return patientDocumentCreateService.update(id, payload as any, signal);
  });

  // Mutation για Delete
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => patientDocumentService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "success" as "success" | "error",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createPatientDocument = async (newDocument: PatientDocumentCreateFormData): Promise<void> => {
    try {
      await createExecution(newDocument);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Το έγγραφο αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updatePatientDocument = async (id: number, updatedData: PatientDocumentCreateFormData): Promise<void> => {
    try {
      await updateDocumentExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση του εγγράφου ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης εγγράφου";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deletePatientDocuments = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    patientDocuments: documents, 
    allDocuments: documents,    
    isLoading: isFetching || isCreating || isDeleting || isUpdating,
    error: getError || createError || deleteError || updateError,
    fetchPatientDocuments: () => refetch(true),
    fetchDocuments: () => refetch(true),
    createPatientDocument,
    updatePatientDocument,
    deletePatientDocuments,
    modalState,
    closeModal,
  };
};